// Sprint 6.9.3 — Génération PDF facture conforme aux usages Algérie
// (NIF / NIS / RC / TVA 19 % / mention "Net à payer en lettres").
//
// jsPDF + jspdf-autotable. Format A4 portrait. Police Helvetica.

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// =====================================================================
// Helpers de formatage
// =====================================================================

const COLOR_INDIGO = '#4F46E5';
const COLOR_SLATE = '#334155';

// Sprint 6.9.3.1 — taux TVA Algérie applicable au B2B formation/services.
export const TVA_DZ_RATE = 0.19;

/**
 * Recalcule TVA et TTC depuis le HT en source de vérité.
 * Les données stockées en DB peuvent être faussées (imports Excel historiques avec taux 9 %).
 * Le PDF doit IGNORER les valeurs stockées et recalculer systématiquement à 19 %.
 */
export function recomputeTvaTtc(montant_ht: number): { tva: number; ttc: number } {
  const tva = Math.round(montant_ht * TVA_DZ_RATE * 100) / 100;
  const ttc = Math.round((montant_ht + tva) * 100) / 100;
  return { tva, ttc };
}

/**
 * Format DZD compatible jsPDF Helvetica.
 * Sprint 6.9.3.1 — `toLocaleString('fr-DZ')` insère des espaces insécables U+00A0
 * que Helvetica ne rend pas → affichage "/" parasite. On remplace toutes les variantes
 * d'espace insécable (U+00A0, U+202F, U+2009) par un espace ASCII standard APRÈS formatage.
 */
export function formatMontantDZD(n: number): string {
  const formatted = n.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const cleaned = formatted.replace(/[\u00A0\u202F\u2009]/g, ' ');
  return `${cleaned} DZD`;
}

export function formatDateFR(date: string | Date | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// =====================================================================
// Conversion nombre → lettres en français (jusqu'à milliards)
// Suffisant pour les montants Algérie.
// =====================================================================

const UNITS = [
  '',
  'un',
  'deux',
  'trois',
  'quatre',
  'cinq',
  'six',
  'sept',
  'huit',
  'neuf',
  'dix',
  'onze',
  'douze',
  'treize',
  'quatorze',
  'quinze',
  'seize',
  'dix-sept',
  'dix-huit',
  'dix-neuf',
];
const TENS = [
  '',
  '',
  'vingt',
  'trente',
  'quarante',
  'cinquante',
  'soixante',
  'soixante',
  'quatre-vingt',
  'quatre-vingt',
];

function lessThan100(n: number): string {
  if (n < 20) return UNITS[n] ?? '';
  const t = Math.floor(n / 10);
  const u = n % 10;
  if (t === 7 || t === 9) {
    return `${TENS[t]}-${UNITS[10 + u]}`;
  }
  if (t === 8 && u === 0) return 'quatre-vingts';
  if (u === 1 && t !== 8) return `${TENS[t]} et un`;
  if (u === 0) return TENS[t] ?? '';
  return `${TENS[t]}-${UNITS[u]}`;
}

function lessThan1000(n: number): string {
  if (n < 100) return lessThan100(n);
  const c = Math.floor(n / 100);
  const r = n % 100;
  const cents = c === 1 ? 'cent' : `${UNITS[c]} cent${r === 0 ? 's' : ''}`;
  if (r === 0) return cents;
  return `${cents} ${lessThan100(r)}`;
}

export function montantEnLettres(n: number): string {
  if (n === 0) return 'zéro dinars algériens';
  const intPart = Math.floor(Math.abs(n));
  const milliards = Math.floor(intPart / 1_000_000_000);
  const millions = Math.floor((intPart % 1_000_000_000) / 1_000_000);
  const milliers = Math.floor((intPart % 1_000_000) / 1000);
  const reste = intPart % 1000;
  const parts: string[] = [];
  if (milliards > 0)
    parts.push(milliards === 1 ? 'un milliard' : `${lessThan1000(milliards)} milliards`);
  if (millions > 0)
    parts.push(millions === 1 ? 'un million' : `${lessThan1000(millions)} millions`);
  if (milliers > 0) parts.push(milliers === 1 ? 'mille' : `${lessThan1000(milliers)} mille`);
  if (reste > 0) parts.push(lessThan1000(reste));
  const txt = parts.join(' ').trim();
  return `${txt} dinars algériens`;
}

// =====================================================================
// Données entrée
// =====================================================================

export interface FactureAlgerieInput {
  reference_facture: string;
  date_emission: string; // YYYY-MM-DD
  date_echeance: string; // YYYY-MM-DD
  type: 'proforma' | 'definitive';
  designation_principale: string;
  participants: number;
  date_formation: string | null;
  lieu: string | null;
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  observation: string | null;
}

export interface EntrepriseEmettrice {
  nom: string;
  adresse?: string | null;
  telephone?: string | null;
  email?: string | null;
  nif?: string | null;
  nis?: string | null;
  rc?: string | null;
  ai?: string | null;
  rib?: string | null;
}

export interface ClientB2B {
  nom: string;
  adresse?: string | null;
  nif?: string | null;
  nis?: string | null;
  rc?: string | null;
}

export interface PdfOptions {
  includeCgv?: boolean;
}

// =====================================================================
// HEADER + FOOTER factorisés
// =====================================================================

function addHeader(doc: jsPDF, emettrice: EntrepriseEmettrice, client: ClientB2B): void {
  // Logo / titre groupe
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(COLOR_INDIGO);
  doc.text('KOST GROUP', 14, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor('#64748B');
  doc.text('Pilotage groupe — Algérie', 14, 23);

  // Bloc émetteur (gauche)
  doc.setFontSize(9);
  doc.setTextColor(COLOR_SLATE);
  let y = 33;
  const emetterLines = [
    emettrice.nom,
    emettrice.adresse ?? '',
    emettrice.telephone ? `Tél : ${emettrice.telephone}` : '',
    emettrice.email ? `Email : ${emettrice.email}` : '',
    emettrice.nif ? `NIF : ${emettrice.nif}` : '',
    emettrice.nis ? `NIS : ${emettrice.nis}` : '',
    emettrice.rc ? `RC : ${emettrice.rc}` : '',
    emettrice.ai ? `AI : ${emettrice.ai}` : '',
  ].filter(Boolean);
  for (const line of emetterLines) {
    doc.text(line, 14, y);
    y += 4;
  }

  // Bloc client (droite)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('CLIENT', 130, 33);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  let cy = 38;
  const clientLines = [
    client.nom,
    client.adresse ?? '',
    client.nif ? `NIF : ${client.nif}` : '',
    client.nis ? `NIS : ${client.nis}` : '',
    client.rc ? `RC : ${client.rc}` : '',
  ].filter(Boolean);
  for (const line of clientLines) {
    doc.text(line, 130, cy);
    cy += 5;
  }
}

function addFooter(
  doc: jsPDF,
  emettrice: EntrepriseEmettrice,
  pageNum: number,
  totalPages: number,
): void {
  const pageHeight = doc.internal.pageSize.getHeight();
  const y = pageHeight - 18;
  doc.setDrawColor('#E2E8F0');
  doc.setLineWidth(0.3);
  doc.line(14, y - 2, 196, y - 2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor('#64748B');
  const ribLine = emettrice.rib ? `RIB : ${emettrice.rib}` : '';
  doc.text(`${emettrice.nom} · ${ribLine}`, 14, y + 1);
  doc.text(`Page ${pageNum} / ${totalPages}`, 196, y + 1, { align: 'right' });
  doc.text(
    'Document généré automatiquement par KOST ERP — sans valeur si non signé et tamponné.',
    14,
    y + 5,
  );
}

// =====================================================================
// MAIN — generateFactureAlgerie
// =====================================================================

export async function generateFactureAlgerie(
  facture: FactureAlgerieInput,
  emettrice: EntrepriseEmettrice,
  client: ClientB2B,
  options: PdfOptions = {},
): Promise<Blob> {
  const doc = new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Sprint 6.9.3.1 — TVA et TTC SOURCE DE VÉRITÉ = HT × 19 %.
  // On ignore facture.montant_tva et facture.montant_ttc passés en input
  // car les données importées historiquement avaient un taux 9 % faux en DB.
  const { tva: tvaCalc, ttc: ttcCalc } = recomputeTvaTtc(facture.montant_ht);

  addHeader(doc, emettrice, client);

  // Titre facture
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(COLOR_INDIGO);
  const title =
    facture.type === 'proforma'
      ? `FACTURE PROFORMA N° ${facture.reference_facture}`
      : `FACTURE N° ${facture.reference_facture}`;
  doc.text(title, pageWidth / 2, 80, { align: 'center' });

  // Dates
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOR_SLATE);
  doc.text(`Date d'émission : ${formatDateFR(facture.date_emission)}`, 14, 90);
  doc.text(`Date d'échéance : ${formatDateFR(facture.date_echeance)}`, 130, 90);

  // Tableau prestations
  const designation = [
    facture.designation_principale,
    facture.participants > 0
      ? `${facture.participants} participant${facture.participants > 1 ? 's' : ''}`
      : '',
    facture.date_formation ? `Date : ${formatDateFR(facture.date_formation)}` : '',
    facture.lieu ? `Lieu : ${facture.lieu}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  autoTable(doc, {
    startY: 98,
    head: [['#', 'Désignation', 'Quantité', 'PU HT', 'Total HT']],
    body: [
      [
        '1',
        designation,
        facture.participants > 0 ? facture.participants.toString() : '1',
        formatMontantDZD(
          facture.participants > 0 ? facture.montant_ht / facture.participants : facture.montant_ht,
        ),
        formatMontantDZD(facture.montant_ht),
      ],
    ],
    headStyles: {
      fillColor: [79, 70, 229], // indigo
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10,
    },
    bodyStyles: { fontSize: 9, valign: 'top', cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 90 },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
    },
    theme: 'grid',
  });

  // Totaux à droite
  const finalY =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 130;
  let yt = finalY + 10;
  const labelX = 130;
  const valueX = 196;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOR_SLATE);
  doc.text('Total HT', labelX, yt);
  doc.text(formatMontantDZD(facture.montant_ht), valueX, yt, { align: 'right' });
  yt += 6;
  doc.text('TVA 19 %', labelX, yt);
  doc.text(formatMontantDZD(tvaCalc), valueX, yt, { align: 'right' });
  yt += 8;

  // Total TTC encadré (recalculé HT + TVA 19%)
  doc.setFillColor(79, 70, 229);
  doc.setDrawColor(79, 70, 229);
  doc.rect(labelX - 2, yt - 5, 68, 9, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(255);
  doc.text('Total TTC', labelX, yt + 1);
  doc.text(formatMontantDZD(ttcCalc), valueX, yt + 1, { align: 'right' });

  // Net à payer en lettres
  yt += 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(COLOR_SLATE);
  doc.text('Arrêtée la présente facture à la somme de :', 14, yt);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  const lettres = montantEnLettres(ttcCalc);
  const wrappedLettres = doc.splitTextToSize(lettres, 180) as string[];
  for (const line of wrappedLettres) {
    yt += 5;
    doc.text(line, 14, yt);
  }

  // Conditions de paiement
  yt += 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Conditions de règlement', 14, yt);
  doc.setFont('helvetica', 'normal');
  yt += 5;
  doc.text(
    'Règlement à 30 jours par virement bancaire. Toute somme non payée à échéance produira',
    14,
    yt,
  );
  yt += 4;
  doc.text(
    'des pénalités de retard équivalentes au taux légal en vigueur (Art. 30 Code de Commerce DZ).',
    14,
    yt,
  );

  if (emettrice.rib) {
    yt += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Coordonnées bancaires', 14, yt);
    doc.setFont('helvetica', 'normal');
    yt += 5;
    doc.text(emettrice.rib, 14, yt);
  }

  // Signature & cachet
  yt += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setDrawColor('#CBD5E1');
  doc.rect(130, yt, 60, 22);
  doc.text('Signature & cachet', 132, yt + 4);

  // Observation si présente
  if (facture.observation) {
    yt += 30;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor('#64748B');
    const obs = doc.splitTextToSize(`Note : ${facture.observation}`, 180) as string[];
    for (const line of obs) {
      yt += 4;
      doc.text(line, 14, yt);
    }
  }

  // CGV page 2
  if (options.includeCgv) {
    doc.addPage();
    addHeader(doc, emettrice, client);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(COLOR_INDIGO);
    doc.text('Conditions Générales de Vente', pageWidth / 2, 80, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLOR_SLATE);
    const cgvText = [
      'Article 1 — Objet',
      'Les présentes conditions régissent les prestations de formation professionnelle dispensées par',
      `${emettrice.nom} en Algérie.`,
      '',
      'Article 2 — Tarifs',
      'Les prix sont exprimés en dinars algériens (DZD), TVA 19 % en sus, conformément',
      "à l'article 21 du Code des Impôts indirects et à la Loi de Finances 2024.",
      '',
      'Article 3 — Modalités de règlement',
      "Les factures sont payables à 30 jours date d'émission, par virement bancaire.",
      "Tout retard de paiement entraîne l'application de pénalités au taux légal en vigueur",
      '(Article 30 du Code de Commerce algérien).',
      '',
      'Article 4 — Annulation',
      'Toute annulation à moins de 7 jours avant la session donne lieu au paiement de 50 %',
      'du montant total. Toute annulation à moins de 48 heures avant la session donne lieu',
      'au paiement de 100 % du montant total, à titre de clause pénale au sens',
      "de l'article 1146 du Code Civil algérien.",
      '',
      'Article 5 — Confidentialité',
      "Les supports de formation sont la propriété intellectuelle de l'émetteur et ne",
      'peuvent être reproduits sans autorisation écrite préalable.',
      '',
      'Article 6 — Loi applicable',
      'Les présentes conditions sont régies par le droit algérien. Tout litige relève',
      "des tribunaux compétents d'Alger.",
    ];
    let cgvY = 90;
    for (const line of cgvText) {
      if (line === '') {
        cgvY += 3;
      } else if (line.startsWith('Article')) {
        doc.setFont('helvetica', 'bold');
        doc.text(line, 14, cgvY);
        doc.setFont('helvetica', 'normal');
        cgvY += 5;
      } else {
        doc.text(line, 14, cgvY);
        cgvY += 4;
      }
    }
  }

  // Footer sur toutes les pages
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    addFooter(doc, emettrice, p, pageCount);
  }

  return doc.output('blob');
}
